import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import SEO from "@/components/SEO";
import { CheckCircle, Upload, ArrowRight, ArrowLeft, CreditCard, Bank, Money, XCircle, CircleNotch } from "@phosphor-icons/react";

const DOC_TYPES = [
  { key: "tc_kimlik", label: "T.C. Kimlik / Yabancı Belge" },
  { key: "biyometrik_foto", label: "Biyometrik Fotoğraf (2 adet)" },
  { key: "ogrenim", label: "Öğrenim Belgesi" },
  { key: "adli_sicil", label: "Adli Sicil Belgesi" },
  { key: "saglik_raporu", label: "Sürücü Olur Sağlık Raporu" },
  { key: "ikametgah", label: "İkametgah Belgesi" },
];

export default function KayitForm() {
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [courses, setCourses] = useState([]);
  const [data, setData] = useState({
    tc_no: "", full_name: "", phone: "", email: "",
    birth_date: "", address: "", course_class: "B",
    education_level: "lise", notes: "", payment_method: "office",
  });
  const [regId, setRegId] = useState(null);
  const [uploads, setUploads] = useState({});
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [tcStatus, setTcStatus] = useState("idle"); // idle | checking | valid | invalid | error

  // Auto-verify TC when TC + name + birth date all filled (with debounce)
  useEffect(() => {
    setTcStatus("idle");
    if (data.tc_no.length !== 11 || !data.full_name.trim() || !data.birth_date) return;
    const parts = data.full_name.trim().split(/\s+/);
    if (parts.length < 2) return;
    const ad = parts.slice(0, -1).join(" ");
    const soyad = parts[parts.length - 1];
    const dogum_yili = parseInt(data.birth_date.split("-")[0]);
    if (!dogum_yili || dogum_yili < 1900) return;

    const t = setTimeout(async () => {
      setTcStatus("checking");
      try {
        const { data: res } = await api.post("/verify-tc", { tc_no: data.tc_no, ad, soyad, dogum_yili });
        setTcStatus(res.valid ? "valid" : "invalid");
      } catch { setTcStatus("error"); }
    }, 700);
    return () => clearTimeout(t);
  }, [data.tc_no, data.full_name, data.birth_date]);

  useEffect(() => {
    if (!authLoading && !user) nav("/kayit-ol");
    if (user) setData(d => ({ ...d, full_name: user.full_name || "", email: user.email || "", phone: user.phone || "" }));
    api.get("/courses").then(r => setCourses(r.data));
  }, [user, authLoading, nav]);

  const submitReg = async () => {
    setLoading(true); setErr("");
    try {
      const { data: res } = await api.post("/registrations", data);
      setRegId(res.id); setStep(3);
    } catch (e) { setErr(e.response?.data?.detail || "Kayıt başarısız"); }
    finally { setLoading(false); }
  };

  const uploadFile = async (docType, file) => {
    if (!file || !regId) return;
    const fd = new FormData(); fd.append("doc_type", docType); fd.append("file", file);
    setLoading(true);
    try {
      const { data: res } = await api.post(`/registrations/${regId}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUploads(u => ({ ...u, [docType]: res.url }));
    } catch (e) { setErr(e.response?.data?.detail || "Yükleme hatası"); }
    finally { setLoading(false); }
  };

  if (authLoading) return <div className="p-12 text-center">Yükleniyor...</div>;

  return (
    <>
      <SEO title="Online Kayıt | Estem Sürücü Kursu" canonical="https://www.estemsurucukursu.com/kayit"/>
      <section className="px-4 md:px-8 py-12 md:py-16 max-w-4xl mx-auto">
        <div className="nb-badge mb-4">Online Kayıt</div>
        <h1 className="font-heading font-bold text-4xl md:text-6xl tracking-tight leading-[1.05]">Birkaç adımda<br/><span className="text-brand-primary">başvurunu tamamla.</span></h1>

        <div className="mt-8 flex items-center gap-2">
          {[1,2,3,4].map(n => (
            <div key={n} className={`flex items-center gap-2 ${n < 4 ? "flex-1" : ""}`}>
              <div className={`w-10 h-10 rounded-full border border-stone-200 flex items-center justify-center font-bold ${step >= n ? "bg-brand-primary text-white" : "bg-white"}`}>{step > n ? <CheckCircle weight="fill" size={20}/> : n}</div>
              {n < 4 && <div className={`flex-1 h-1 ${step > n ? "bg-brand-primary" : "bg-brand-ink/20"}`}/>}
            </div>
          ))}
        </div>

        {err && <div className="mt-6 p-4 bg-brand-error/10 border-2 border-brand-error rounded-xl text-brand-error font-bold">{err}</div>}

        <div className="nb-card bg-white p-6 md:p-10 mt-8" data-testid="kayit-form">
          {step === 1 && (
            <>
              <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase">1. Kişisel Bilgiler</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                <div><label className="nb-label">T.C. Kimlik No</label>
                  <div className="relative mt-1">
                    <input required maxLength={11} pattern="[0-9]{11}" value={data.tc_no} onChange={e=>setData({...data, tc_no:e.target.value.replace(/\D/g,'')})} className={`nb-input pr-10 ${tcStatus==="valid"?"border-brand-success":tcStatus==="invalid"?"border-brand-error":""}`} data-testid="kayit-tc"/>
                    {tcStatus === "checking" && <CircleNotch weight="bold" className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-primary animate-spin" size={20}/>}
                    {tcStatus === "valid" && <CheckCircle weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-success" size={22}/>}
                    {tcStatus === "invalid" && <XCircle weight="fill" className="absolute right-3 top-1/2 -translate-y-1/2 text-brand-error" size={22}/>}
                  </div>
                  {tcStatus === "valid" && <div className="text-xs text-brand-success font-semibold mt-1">✓ NVI ile doğrulandı</div>}
                  {tcStatus === "invalid" && <div className="text-xs text-brand-error font-semibold mt-1">TC/ad/soyad/doğum yılı eşleşmiyor</div>}
                </div>
                <div><label className="nb-label">Ad Soyad</label><input required value={data.full_name} onChange={e=>setData({...data, full_name:e.target.value})} className="nb-input mt-1" data-testid="kayit-name"/></div>
                <div><label className="nb-label">Doğum Tarihi</label><input required type="date" value={data.birth_date} onChange={e=>setData({...data, birth_date:e.target.value})} className="nb-input mt-1" data-testid="kayit-birth"/></div>
                <div><label className="nb-label">Telefon</label><input required value={data.phone} onChange={e=>setData({...data, phone:e.target.value})} className="nb-input mt-1" data-testid="kayit-phone"/></div>
                <div className="md:col-span-2"><label className="nb-label">E-posta</label><input required type="email" value={data.email} onChange={e=>setData({...data, email:e.target.value})} className="nb-input mt-1" data-testid="kayit-email"/></div>
                <div className="md:col-span-2"><label className="nb-label">Adres</label><textarea required rows={3} value={data.address} onChange={e=>setData({...data, address:e.target.value})} className="nb-input mt-1" data-testid="kayit-address"/></div>
                <div className="md:col-span-2"><label className="nb-label">Öğrenim Durumu</label>
                  <select value={data.education_level} onChange={e=>setData({...data, education_level:e.target.value})} className="nb-input mt-1" data-testid="kayit-edu">
                    <option value="ilkokul">İlkokul</option><option value="ortaokul">Ortaokul</option><option value="lise">Lise</option><option value="onlisans">Ön Lisans</option><option value="lisans">Lisans</option><option value="lisansustu">Lisansüstü</option>
                  </select>
                </div>
              </div>
              <button onClick={()=>setStep(2)} disabled={tcStatus === "invalid"} className="nb-btn mt-6 w-full" data-testid="kayit-next-1">
                {tcStatus === "invalid" ? "TC bilgileriniz eşleşmiyor" : "Devam"} <ArrowRight weight="bold"/>
              </button>
            </>
          )}

          {step === 2 && (
            <>
              <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase">2. Sınıf Seçimi</h2>
              <div className="mt-6 grid md:grid-cols-2 gap-4">
                {courses.map(c => (
                  <label key={c.code} className={`p-5 border border-stone-200 rounded-2xl cursor-pointer transition-all ${data.course_class === c.code ? "bg-brand-primary text-white " : "bg-white hover:bg-brand-primarySubtle"}`}>
                    <input type="radio" name="class" value={c.code} checked={data.course_class === c.code} onChange={e=>setData({...data, course_class:e.target.value})} className="sr-only" data-testid={`kayit-class-${c.code}`}/>
                    <div className="flex items-center justify-between">
                      <div className="font-heading font-bold text-lg uppercase">{c.name}</div>
                      <div className="font-heading font-bold text-xl">{c.price.toLocaleString("tr-TR")} ₺</div>
                    </div>
                    <div className="text-sm opacity-80 mt-1">{c.duration}</div>
                  </label>
                ))}
              </div>
              <div className="mt-4"><label className="nb-label">Notunuz (opsiyonel)</label><textarea rows={2} value={data.notes} onChange={e=>setData({...data, notes:e.target.value})} className="nb-input mt-1" data-testid="kayit-notes"/></div>
              <div className="mt-6">
                <label className="nb-label">Ödeme Yöntemi</label>
                <div className="mt-2 grid md:grid-cols-3 gap-3">
                  {[
                    { v: "office", l: "Ofiste", i: Money },
                    { v: "transfer", l: "Havale/EFT", i: Bank },
                    { v: "online", l: "Online (yakında)", i: CreditCard, dis: true },
                  ].map(p => (
                    <label key={p.v} className={`p-4 border border-stone-200 rounded-xl cursor-pointer text-center font-bold ${data.payment_method === p.v ? "bg-brand-ink text-white" : "bg-white"} ${p.dis ? "opacity-50 cursor-not-allowed" : ""}`}>
                      <input type="radio" disabled={p.dis} name="pm" value={p.v} checked={data.payment_method === p.v} onChange={e=>setData({...data, payment_method:e.target.value})} className="sr-only"/>
                      <p.i size={24} weight="bold" className="mx-auto mb-1"/>{p.l}
                    </label>
                  ))}
                </div>
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={()=>setStep(1)} className="nb-btn-outline" data-testid="kayit-prev-2"><ArrowLeft weight="bold"/> Geri</button>
                <button onClick={submitReg} disabled={loading} className="nb-btn flex-1" data-testid="kayit-submit-2">{loading ? "Kaydediliyor..." : "Kaydı Oluştur"} <ArrowRight weight="bold"/></button>
              </div>
            </>
          )}

          {step === 3 && (
            <>
              <h2 className="font-heading font-bold text-2xl md:text-3xl uppercase">3. Belge Yükleme</h2>
              <p className="mt-2 text-brand-muted">Aşağıdaki belgeleri JPG, PNG veya PDF olarak yükleyin. Tüm belgeler güvenli sunucumuzda saklanır.</p>
              <div className="mt-6 space-y-3">
                {DOC_TYPES.map(d => (
                  <div key={d.key} className="p-4 border border-stone-200 rounded-xl flex items-center gap-4 bg-brand-bg">
                    <div className="flex-1">
                      <div className="font-bold">{d.label}</div>
                      {uploads[d.key] && <div className="text-xs text-brand-success font-bold flex items-center gap-1 mt-1"><CheckCircle weight="fill"/> Yüklendi</div>}
                    </div>
                    <label className={`nb-btn-outline cursor-pointer !px-4 !py-2 !text-xs ${uploads[d.key] ? "bg-brand-success text-white" : ""}`} data-testid={`upload-${d.key}`}>
                      <Upload weight="bold" size={16}/> {uploads[d.key] ? "Değiştir" : "Yükle"}
                      <input type="file" accept=".jpg,.jpeg,.png,.pdf" className="hidden" onChange={e=>uploadFile(d.key, e.target.files[0])}/>
                    </label>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex gap-3">
                <button onClick={()=>setStep(4)} className="nb-btn flex-1" data-testid="kayit-next-3">Devam <ArrowRight weight="bold"/></button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <div className="text-center">
                <div className="w-20 h-20 bg-brand-success text-white border border-stone-200 rounded-full flex items-center justify-center mx-auto mb-6"><CheckCircle size={48} weight="fill"/></div>
                <h2 className="font-heading font-bold text-3xl md:text-4xl uppercase">Başvurun Alındı!</h2>
                <p className="mt-3 text-brand-muted max-w-md mx-auto">Kayıt numaranız: <strong className="text-brand-ink">{regId?.slice(0,8).toUpperCase()}</strong>. En geç 48 saat içinde sizi arayacağız.</p>

                <div className="mt-8 grid md:grid-cols-3 gap-3 text-left">
                  {[{t:"Sıradaki Adımlar"},{t:"Telefonla Doğrulama"},{t:"Sınıfa Yerleştirme"}].map((s,i)=>(
                    <div key={i} className="p-4 border border-stone-200 rounded-xl bg-brand-bg">
                      <div className="text-2xl font-bold text-brand-primary">{i+1}</div>
                      <div className="font-bold text-sm">{s.t}</div>
                    </div>
                  ))}
                </div>
                <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
                  <Link to="/panelim" className="nb-btn" data-testid="kayit-go-dashboard">Panele Git</Link>
                  <Link to="/" className="nb-btn-outline">Ana Sayfa</Link>
                </div>
              </div>
            </>
          )}
        </div>
      </section>
    </>
  );
}
